import React, { useEffect, useState } from "react";
import { Button, Input, Select } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";

import { searchProcedures } from "./estimateClaimsApi";

/**
 * The procedures on a submission, and the tooth or quadrant each applies to.
 *
 * A small table with an add-row button: press "Add procedure" and a new row
 * appears with the procedure search in its first cell and the tooth field beside
 * it; pick a code and the row settles into code + description + tooth. The
 * earlier layout — one search box that quietly appended rows beneath it — read
 * as a single pick, and people did not find out they could add a second one.
 *
 * Optional throughout: a submission is still sendable with none, so nothing here
 * gates the request.
 *
 * Search is server-side and scoped to the PATIENT's office — procedure
 * catalogues are per-office and the header's office is not always the patient's.
 */
export default function EstimateProcedures({ patientId, value, onChange, disabled, getPopupContainer }) {
  const rows = value || [];
  // The pending row: shown with the search in it until a code is picked.
  const [adding, setAdding] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced] = useDebounce(term, 300);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(
    function () {
      if (!patientId || !adding) return undefined;
      let cancelled = false;
      setLoading(true);
      (async function () {
        const results = await searchProcedures(patientId, debounced);
        if (cancelled) return;
        setOptions(results);
        setLoading(false);
      })();
      return function () { cancelled = true; };
    },
    [patientId, debounced, adding],
  );

  const chosen = {};
  rows.forEach(function (r) { chosen[r.code] = true; });

  function pick(procedureId) {
    const picked = options.find(function (o) { return o.id === procedureId; });
    if (picked && !chosen[picked.code]) {
      onChange(rows.concat([{ code: picked.code, description: picked.description, tooth: "" }]));
    }
    setTerm("");
    setAdding(false);
  }

  function setTooth(index, tooth) {
    onChange(rows.map(function (r, i) {
      return i === index ? Object.assign({}, r, { tooth: tooth }) : r;
    }));
  }

  function remove(index) {
    onChange(rows.filter(function (_r, i) { return i !== index; }));
  }

  const showTable = rows.length > 0 || adding;

  return (
    <div className="est-procs">
      <div className="est-field-label">
        Procedures <span className="est-procs-optional">optional</span>
      </div>

      {showTable ? (
        <div className="est-proc-table">
          <div className="est-proc-row est-proc-row--head">
            <span>Procedure</span>
            <span>Tooth / quad</span>
            <span aria-hidden="true" />
          </div>

          {rows.map(function (row, index) {
            return (
              <div className="est-proc-row" key={row.code + ":" + index}>
                <div className="est-proc-code">
                  <span className="est-proc-code-value">{row.code}</span>
                  {row.description ? (
                    <span className="est-proc-code-desc">{row.description}</span>
                  ) : null}
                </div>
                <Input
                  className="est-proc-tooth"
                  value={row.tooth}
                  onChange={function (e) { setTooth(index, e.target.value); }}
                  disabled={disabled}
                  placeholder="e.g. 16 or UR"
                  maxLength={32}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  disabled={disabled}
                  onClick={function () { remove(index); }}
                  aria-label={"Remove " + row.code}
                />
              </div>
            );
          })}

          {adding ? (
            <div className="est-proc-row est-proc-row--new">
              <Select
                showSearch
                autoFocus
                defaultOpen
                value={undefined}
                placeholder="Search a procedure code or name"
                // Server-side search: the option labels are already the matches,
                // so antd must not filter them again against the raw term.
                filterOption={false}
                onSearch={setTerm}
                onChange={pick}
                loading={loading}
                notFoundContent={loading ? "Searching…" : "No matching procedures"}
                style={{ width: "100%" }}
                getPopupContainer={getPopupContainer}
                options={options
                  .filter(function (o) { return !chosen[o.code]; })
                  .map(function (o) {
                    return {
                      value: o.id,
                      label: o.description ? `${o.code} — ${o.description}` : o.code,
                    };
                  })}
              />
              <Input className="est-proc-tooth" disabled placeholder="Tooth" />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={function () { setAdding(false); setTerm(""); }}
                aria-label="Cancel adding a procedure"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        className="est-procs-add"
        disabled={disabled || adding}
        onClick={function () { setAdding(true); }}
      >
        Add procedure
      </Button>
    </div>
  );
}
